import { PopupType } from '../../../components/popup/types';
import { Logger } from '../../../logger/Logger';
import { RoundIntegrityWatcher, RoundIntegrityWatcherPhases } from '../../../logger/RoundIntegrityWatcher';
import { fetchUserInfoAction } from '../../../store/apiActions/fetchUserInfoAction';
import type { CoreStoreType } from '../../../store/CoreStore';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { AutoplayStatus } from '../../../store/slices/autoplay/types';
import { gameStateActions } from '../../../store/slices/game/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { toastActions } from '../../../store/slices/toast/slice';
import type { GameCommunicationMessageType, IGameCommunicationProcessor } from '../types';

export interface WinMessage extends GameCommunicationMessageType {
  // TODO: document me in storybook
  gameId: string;
  mCap: boolean; // Maximum casino payout cap reached
  megawin: boolean; // is this win a megawin (special win animations)
  nwb: string; // Net Win Balance
  seq: number; // web socket sequence number
  table: string; // Table id
  win: string; // Number in a string format
}

export class GameCommunicationWinProcessor implements IGameCommunicationProcessor {
  messageName = 'win';

  process(message: WinMessage, store: CoreStoreType): void {
    RoundIntegrityWatcher.setPhase(RoundIntegrityWatcherPhases.Win);

    // TODO: check if this is needed - do we receive it not as a string with boolean value or as different type in some games?
    if (typeof message.megawin === 'string') {
      message.megawin = !!message.megawin;
    }
    if (message.nwb) {
      store.dispatch(gameStateActions.setNetPosition(parseFloat(message.nwb)));
    }
    const win = message?.win ? parseFloat(message.win) : 0;

    store.dispatch(gameStateActions.setWinResult({ amount: win, updateTimestamp: Date.now() }));

    const { autoplay, bets, config } = store.getState();
    const JSESSIONID = config.session?.JSESSIONID;
    const baseUrl = config.session?.metaServer;
    if (JSESSIONID && baseUrl) {
      store.dispatch(fetchUserInfoAction({ JSESSIONID, force: true, baseUrl, essential: true }));
    }
    if (autoplay.isInProgress || (autoplay.autoplayStatus === AutoplayStatus.Paused && !!bets.acceptedBets.length)) {
      const currentPopup = store.getState().popup.currentPopup?.popupName;
      if (currentPopup === 'AutoplayModified') {
        store.dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
        store.dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
      }

      if (!autoplay.isLaunchedMidRound) {
        const netChange = win - (bets.overridingTotalBet ?? 0);
        store.dispatch(autoplayActions.updateBalanceChange(netChange));
      }
      if (autoplay.roundsLeft <= 0) {
        store.dispatch(toastActions.createToast('AUTOPLAY_COMPLETED'));
        store.dispatch(autoplayActions.stop());
        Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, 'Completed', false);
      }
      this.checkLimits(store, message);
    }
  }

  /**
   * Check the game state after the win for limit reaching (autoplay limits, casino cap limit etc.)
   * AUTOPLAY SINGLE WIN LIMIT
   * AUTOPLAY BALANCE INCREASE LIMIT
   * AUTOPLAY BALANCE DECREASE LIMIT
   */
  checkLimits = (store: CoreStoreType, message: WinMessage): void => {
    const { autoplay } = store.getState();
    if (autoplay.isLaunchedMidRound) return;
    if (
      (autoplay.singleWinLimit && autoplay.singleWinLimit <= parseFloat(message.win)) ||
      (autoplay.balanceIncreaseLimit && autoplay.balanceIncreaseLimit <= autoplay.balanceChange) ||
      (autoplay.balanceDecreaseLimit && -autoplay.balanceDecreaseLimit >= autoplay.balanceChange)
    ) {
      this.stopAutoplay(store);
    }
  };

  stopAutoplay = (store: CoreStoreType): void => {
    store.dispatch(autoplayActions.stop());
    store.dispatch(popupActions.pushPopup(PopupType.AutoplayStopped));
  };
}
